import * as R from 'ramda';
import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface ScrollbarSizes {
  width: number;
  height: number;
}

// Given the ref of a DOM node, find the size of scroll bars rendered within it.
export const useScrollbarSizes = (
  ref: React.MutableRefObject<HTMLDivElement | null>
): ScrollbarSizes => {
  const [sizes, setScrollbarWidths] = useState({
    width: 0,
    height: 0
  });
  // After the component loads, do this. If we ever change the ref, we'll re-compute, but if the ref stays the same
  // (points to the same element) this will only run once.
  useLayoutEffect(() => {
    let frameId: number | undefined;

    const container = ref.current || document.getElementById('root');
    if (container) {
      // Make a div
      const detector = document.createElement('div');
      // Make it a somewhat arbitrary size that's bigger than we expect the scroll bars to be.
      detector.style.width = '1000px';
      detector.style.height = '1000px';
      // Make it overflow: scroll. This gives it scroll bars even if it has nothing to scroll (which it won't because it
      // will be empty)
      detector.style.overflow = 'scroll';
      // This should get added and removed from the DOM before the browser repaints. In the off chance it doesn't, make
      // sure it's invisible.
      detector.style.visibility = 'hidden';
      // Add it to the ref component, or document if there is no ref component
      container.appendChild(detector);

      // offsetWidth is the space outside the div, clientWidth is the space inside it. So the difference is the size of
      // the scroll bar!
      const scrollbarSizes = {
        width: detector.offsetWidth - detector.clientWidth,
        height: detector.offsetHeight - detector.clientHeight
      };

      // Delete our detector.
      container.removeChild(detector);

      // Save the values in a callback to avoid synchronous setState within an effect.
      frameId = requestAnimationFrame(() => setScrollbarWidths(scrollbarSizes));
    }

    return () => {
      if (frameId !== undefined) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [ref]);
  return sizes;
};

export const useIsMounted = (): (() => boolean) => {
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Reset on (re)mount so the ref is correct under StrictMode's dev
    // mount→unmount→remount cycle and any genuine remount — otherwise the
    // cleanup leaves it permanently false.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const getIsMounted = useCallback(() => isMountedRef.current, []);
  return getIsMounted;
};

interface Position {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

type ResetPosition = () => void;

export const useRenderedPosition = (
  ref: React.MutableRefObject<HTMLElement | null>
): [Position, ResetPosition] => {
  const [position, setPositionRaw] = useState({ top: 0, right: 0, bottom: 0, left: 0 });
  const setPosition = useCallback(() => {
    if (ref.current) {
      setPositionRaw(
        R.pick(['top', 'right', 'bottom', 'left'], ref.current.getBoundingClientRect())
      );
    }
  }, [ref]);
  useLayoutEffect(() => {
    setPosition();
  }, [setPosition]);
  return [position, setPosition];
};

interface ContentBoxSize {
  width: number;
  height: number;
}

export const getContentBoxSize = (element: HTMLElement): ContentBoxSize => {
  const { paddingLeft, paddingRight, paddingTop, paddingBottom } = getComputedStyle(element);
  return {
    width: element.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight),
    height: element.clientHeight - parseFloat(paddingTop) - parseFloat(paddingBottom)
  };
};

// AutoSizer reports its first size from a ResizeObserver plus setTimeout, so its children paint
// once at 0x0. This reads the same content-box size before that first paint.
export const usePrePaintSize = (
  ref: RefObject<HTMLElement | null>,
  enabled = true
): ContentBoxSize | undefined => {
  const [size, setSize] = useState<ContentBoxSize>();
  useLayoutEffect(() => {
    if (!enabled || !ref.current) return;
    setSize(getContentBoxSize(ref.current));
  }, [ref, enabled]);
  return size;
};
